import { useQueryClient } from '@tanstack/react-query';
import { useModeAwareQuery, useModeAwareMutation } from './useModeAwareQuery';
import { useApi } from './useApi';

export interface GeneralSettings {
  onStopNotesEnabled: boolean;
  categoryKeys: {
    userSortKey1Label: string;
    userSortKey2Label: string;
    userSortKey3Label: string;
    userSortKey4Label: string;
  };
}

export interface UpsertGeneralSettingsRequest {
  onStopNotesEnabled: boolean;
  userSortKey1Label: string;
  userSortKey2Label: string;
  userSortKey3Label: string;
  userSortKey4Label: string;
}

const DEFAULT_SETTINGS: GeneralSettings = {
  onStopNotesEnabled: false,
  categoryKeys: {
    userSortKey1Label: 'User Sort Key 1',
    userSortKey2Label: 'User Sort Key 2',
    userSortKey3Label: 'User Sort Key 3',
    userSortKey4Label: 'User Sort Key 4',
  },
};

/**
 * Hook to fetch general settings.
 * Handles both Demo and Live modes.
 */
export function useGeneralSettings() {
  const { apiGet } = useApi();

  return useModeAwareQuery<GeneralSettings, Error>(
    ['general-settings'],
    async ({ signal }) => {
      const response = await apiGet<GeneralSettings>(
        `/api/v1/settings/general/customers`,
        undefined,
        signal,
      );
      return response;
    },
    DEFAULT_SETTINGS,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

/**
 * Hook to upsert general (customer) settings.
 * Handles both Demo and Live modes.
 */
export function useUpsertGeneralSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<GeneralSettings, Error, UpsertGeneralSettingsRequest>(
    async (data) => {
      return apiPut<GeneralSettings>('/api/v1/settings/general/customers', data);
    },
    async (data) => {
      return {
        onStopNotesEnabled: data.onStopNotesEnabled,
        categoryKeys: {
          userSortKey1Label: data.userSortKey1Label,
          userSortKey2Label: data.userSortKey2Label,
          userSortKey3Label: data.userSortKey3Label,
          userSortKey4Label: data.userSortKey4Label,
        },
      };
    },
    {
      onSuccess: (data) => {
        queryClient.setQueriesData<GeneralSettings>(
          { queryKey: ['general-settings'] },
          data,
        );
      },
    },
  );
}
